import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { isAxiosError } from 'axios';
import { useAuthStore } from '@/entities/Session';
import { useUserStore } from '@/entities/User';
import { useCustomToast } from '@/shared/lib';
import { ERROR_MESSAGES } from '@/shared/config';
import { useValidation } from './useValidation';

interface LoginCredentials {
  username: string;
  password: string;
  rememberMe: boolean;
}

interface UseLoginOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => boolean;
}

interface LoginFormState {
  username: string;
  password: string;
}

interface LoginErrors {
  username: string;
  password: string;
}

interface ApiErrorResponse {
  success: boolean;
  message: string;
}

export function useLogin(options: UseLoginOptions = {}) {
  const router = useRouter();
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const toast = useCustomToast();
  const { validatePassword, validateUsername } = useValidation();

  const form = reactive<LoginFormState>({
    username: '',
    password: '',
  });

  const errors = reactive<LoginErrors>({
    username: '',
    password: '',
  });

  const rememberMe = ref(false);
  const isLoginLoading = ref(false);

  // Toast notifications
  const showLoginSuccessToast = (): void => {
    toast.showSuccessToast({
      title: 'Login feito com sucesso!',
      message: 'Bem-vindo (a) ao Gestor de Documentos 2.0!',
      type: 'success',
    });
  };

  const showNoMembershipToast = (): void => {
    toast.showErrorToast({
      title: 'Erro',
      message: 'Usuário não possui vínculos com uma filial',
      type: 'error',
    });
  };

  const showInvalidCredentialsToast = (): void => {
    toast.showErrorToast({
      title: 'Erro',
      message: ERROR_MESSAGES.INVALID_CREDENTIALS,
      type: 'error',
    });
  };

  const showServerErrorToast = (): void => {
    toast.showErrorToast({
      title: 'Erro',
      message: 'Servidor indisponível. Por favor, tente novamente mais tarde.',
      type: 'error',
    });
  };

  const showUnknownErrorToast = (): void => {
    toast.showErrorToast({
      title: 'Erro',
      message: 'Erro inesperado. Por favor, tente novamente mais tarde.',
      type: 'error',
    });
  };

  const showLoginFailToast = (): void => {
    toast.showErrorToast(
      {
        title: 'Erro',
        message: 'Senha incorreta. Verifique suas credenciais e tente novamente.',
        type: 'error',
      },
    );
  };

  // Validation helpers
  const validateForm = (): boolean => {
    errors.password = validatePassword(form.password);
    errors.username = validateUsername(form.username);
    return !errors.username && !errors.password;
  };

  const handleBlurUsername = (): void => {
    errors.username = validateUsername(form.username);
  };

  const handleBlurPassword = (): void => {
    errors.password = validatePassword(form.password);
  };

  // Default error handler
  const defaultErrorHandler = (error: unknown): boolean => {
    if (isAxiosError<ApiErrorResponse>(error)) {
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.message;
      const isInvalidCredentialsError =
        errorMessage === ERROR_MESSAGES.INVALID_CREDENTIALS &&
        (statusCode === undefined || statusCode === 400 || statusCode === 401);

      if (isInvalidCredentialsError) {
        errors.password = 'Usuário ou senha incorretos';
        errors.username = ' ';
        showInvalidCredentialsToast();
        return true;
      } else if (errorMessage === ERROR_MESSAGES.NO_BRANCH_MEMBERSHIP) {
        showNoMembershipToast();
        return true;
      } else if (error.response?.status && error.response.status >= 500) {
        showServerErrorToast();
        return true;
      }
    }
    return false;
  };

  // Main login handler
  const handleSubmit = async (): Promise<void> => {
    if (!validateForm()) return;

    isLoginLoading.value = true;

    try {
      const credentials: LoginCredentials = {
        username: form.username.trim(),
        password: form.password,
        rememberMe: rememberMe.value,
      };

      await authStore.login(credentials);

      if (authStore.mustChangePassword) {
        return;
      }

      if (options.onSuccess) {
        options.onSuccess();
      } else {
        router.push('/');
        showLoginSuccessToast();
      }
    } catch (error) {
      // Try custom error handler first
      const customHandled = options.onError?.(error) ?? false;

      // If not handled by custom handler, use default
      if (!customHandled) {
        const defaultHandled = defaultErrorHandler(error);
        if (!defaultHandled) {
          showUnknownErrorToast();
        }
      }
    } finally {
      isLoginLoading.value = false;
    }
  };

  const mustChangePassword = computed(() => authStore.mustChangePassword);

  const handlePasswordChange = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<string | undefined> => {
    isLoginLoading.value = true;
    try {
      await authStore.changePassword(currentPassword, newPassword);
      router.push('/');
      showLoginSuccessToast();
      return undefined;
    } catch (error) {
      if (isAxiosError<ApiErrorResponse>(error)) {
        return error.response?.data?.message ?? 'Erro inesperado. Por favor, tente novamente mais tarde.';
      }
      return 'Erro inesperado. Por favor, tente novamente mais tarde.';
    } finally {
      isLoginLoading.value = false;
    }
  };

  const fetchCurrentUser = async (): Promise<string> => {
    try {
      const user = await userStore.me();
      const username = user.data.data.username ?? '';
      form.username = username;
      return username;
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to fetch current user:', error);
      }
      return '';
    }
  };

  return {
    form,
    errors,
    rememberMe,
    isLoginLoading,
    mustChangePassword,

    validateForm,
    handleBlurUsername,
    handleBlurPassword,

    handleSubmit,
    handlePasswordChange,
    fetchCurrentUser,

    showLoginSuccessToast,
    showNoMembershipToast,
    showInvalidCredentialsToast,
    showServerErrorToast,
    showUnknownErrorToast,
    showLoginFailToast,
  };
}
